import React, { useState, ReactNode, forwardRef, CSSProperties, useMemo } from "react";
import { motion, AnimatePresence } from "motion/react";
import Button from "../Button/Button";
import { getColorBaseTokenValue } from "../../design-tokens";
import styles from "./FilterPanel.module.css";

export interface FilterPanelProps {
  /**
   * Título da seção de filtro exibido no header
   */
  title: string;

  /**
   * Indica se o ícone de filtro deve ser exibido no header
   * @default false
   */
  showFilterIcon?: boolean;

  /**
   * Conteúdo do painel (campos de filtro usando Input)
   */
  children: ReactNode;

  /**
   * Callback executado ao clicar no botão "Aplicar Filtros"
   */
  onApply: () => void;

  /**
   * Callback executado ao clicar no botão "Limpar"
   */
  onClear: () => void;

  /**
   * Estado inicial do painel (expandido ou recolhido)
   * @default true
   */
  defaultExpanded?: boolean;

  /**
   * Classes CSS adicionais para o container
   */
  className?: string;
}

const FilterPanel = forwardRef<HTMLDivElement, FilterPanelProps>(
  (
    {
      title,
      showFilterIcon = false,
      children,
      onApply,
      onClear,
      defaultExpanded = true,
      className
    },
    ref
  ) => {
    const [isExpanded, setIsExpanded] = useState(defaultExpanded);

    const toggleExpanded = (): void => {
      setIsExpanded((prev) => !prev);
    };

    const styleVariables = useMemo(
      (): CSSProperties => ({
        "--ds-filterpanel-icon-size": "1.25rem",
        "--ds-filterpanel-icon-color": getColorBaseTokenValue("color.neutral.700"),
        "--ds-filterpanel-icon-secondary": getColorBaseTokenValue("color.neutral.500"),
        "--ds-filterpanel-title-color": getColorBaseTokenValue("color.neutral.900"),
        "--ds-filterpanel-bg": getColorBaseTokenValue("color.neutral.000"),
        "--ds-filterpanel-bg-subtle": getColorBaseTokenValue("color.neutral.100"),
        "--ds-filterpanel-bg-hover": getColorBaseTokenValue("color.neutral.100"),
        "--ds-filterpanel-border": getColorBaseTokenValue("color.neutral.300"),
      } as CSSProperties),
      []
    );

    const containerClasses = [styles.container, className].filter(Boolean).join(" ");
    const headerClasses = [styles.header, !isExpanded && styles.headerCollapsed].filter(Boolean).join(" ");

    return (
      <div ref={ref} className={containerClasses} style={styleVariables}>
        <div className={headerClasses} onClick={toggleExpanded} role="button" tabIndex={0}>
          {showFilterIcon && (
            <span
              className="material-symbols-outlined"
              style={{ fontSize: "var(--ds-filterpanel-icon-size)" }}
              aria-hidden="true"
            >
              filter_alt
            </span>
          )}
          <h3 className={styles.title}>{title}</h3>
          <span
            className="material-symbols-outlined"
            style={{ fontSize: "var(--ds-filterpanel-icon-size)" }}
            aria-hidden="true"
          >
            {isExpanded ? "keyboard_arrow_up" : "keyboard_arrow_down"}
          </span>
        </div>

        <AnimatePresence initial={false}>
          {isExpanded && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: "auto", opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{ duration: 0.3, ease: "easeInOut" }}
              style={{ overflow: "hidden" }}
            >
              <div className={styles.filterArea}>{children}</div>

              <div className={styles.actions}>
                <Button label="Limpar" severity="neutral" variant="text" onClick={onClear} />
                <Button label="Aplicar Filtros" severity="secondary" onClick={onApply} />
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    );
  }
);

FilterPanel.displayName = "FilterPanel";

export default FilterPanel;
